import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, TrendingUp, AlertCircle, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';
import OnboardingTour from '../components/OnboardingTour';
import { getVersionString } from '../config/version';
import UserNotificationCenter from '../components/UserNotificationCenter';
import CommissionWidget from '../components/CommissionWidget';
import { LOGO_DARK_BG } from '../config/branding';

const DashboardPage = () => {
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  
  // TODO: Get partner_id from session/auth
  const partnerId = new URLSearchParams(window.location.search).get('partner_id') || localStorage.getItem('partner_id');

  useEffect(() => {
    if (!partnerId) {
      navigate('/partner/registrieren');
      return;
    }
    
    loadDashboard();
    
    // M11.4: Lazy reminder check
    checkDocumentReminders();
    
    // Check if onboarding should be shown - only after cookie decision
    const cookieConsent = localStorage.getItem('cookie_consent');
    const hasSeenOnboarding = localStorage.getItem(`onboarding_completed_${partnerId}`);
    
    if (cookieConsent && !hasSeenOnboarding) {
      // Delay onboarding to ensure cookie banner is hidden first
      setTimeout(() => setShowOnboarding(true), 500);
    }
  }, [partnerId]);

  const loadDashboard = async () => {
    try {
      const token = localStorage.getItem('partner_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // Load partner
      const partnerRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partners/${partnerId}`);
      const partnerData = await partnerRes.json();
      setPartner(partnerData);
      localStorage.setItem('partner_id', partnerId);
      
      // Load cases - with authentication
      const casesRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cases/partner/${partnerId}`, {
        headers
      });
      
      if (casesRes.status === 401 || casesRes.status === 403) {
        // Token invalid or access denied - redirect to login
        localStorage.removeItem('partner_token');
        navigate('/portal/login');
        return;
      }
      
      const casesData = await casesRes.json();
      setCases(casesData);
    } catch (error) {
      console.error('Load failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkDocumentReminders = async () => {
    try {
      // Lazy check - trigger reminder logic
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/check-reminders`, {
        method: 'POST'
      });
    } catch (error) {
      // Silent fail
    }
  };

  const handleOnboardingComplete = (skipped = false) => {
    localStorage.setItem(`onboarding_completed_${partnerId}`, 'true');
    setShowOnboarding(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p>Lädt...</p>
      </div>
    );
  }

  // Count open requests
  const openRequestsCount = cases.filter(c => c.status === 'rueckfrage_offen').length;

  // Sort cases: rueckfrage_offen first
  const sortedCases = [...cases].sort((a, b) => {
    if (a.status === 'rueckfrage_offen' && b.status !== 'rueckfrage_offen') return -1;
    if (a.status !== 'rueckfrage_offen' && b.status === 'rueckfrage_offen') return 1;
    return 0;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Onboarding Tour */}
      {showOnboarding && <OnboardingTour onComplete={handleOnboardingComplete} />}

      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-4">
              <img src={LOGO_DARK_BG} alt="Dein Gutachten Direkt" className="h-10" />
              <h1 className="text-xl sm:text-2xl font-bold text-white">Partner Portal</h1>
            </div>
            <div className="flex items-center gap-3 sm:gap-6 flex-wrap">
              <UserNotificationCenter 
                token={localStorage.getItem('partner_token')} 
                apiEndpoint="/api/partners/notifications/my"
                portalType="partner"
              />
              <button
                onClick={() => navigate(`/portal/provisionen?partner_id=${partnerId}`)}
                className="provisions-link text-brand-green hover:text-green-400 font-semibold text-xs sm:text-sm"
              >
                Provisionen
              </button>
              <button
                onClick={() => navigate(`/portal/profil?partner_id=${partnerId}`)}
                className="text-brand-green hover:text-green-400 font-semibold text-xs sm:text-sm"
              >
                Profil
              </button>
              <button
                onClick={() => navigate('/portal/benachrichtigungen')}
                className="text-gray-300 hover:text-brand-green font-medium text-xs sm:text-sm flex items-center gap-1"
                data-testid="notification-settings-link"
              >
                <Bell className="w-4 h-4" />
                Einstellungen
              </button>
              <div className="text-sm text-gray-300">
                {partner?.company_name || partner?.email}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Stats */}
        <div className="dashboard-stats grid md:grid-cols-4 gap-6 mb-12">
          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-brand-green/10 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Fälle dieses Monats</p>
                <p className="text-2xl font-bold text-gray-900">{partner?.current_month_cases || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-brand-green/10 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Aktuelles Provisionslevel</p>
                <p className="text-2xl font-bold text-brand-green">{partner?.provision_level || 30}%</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-brand-green/10 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Gesamt Fälle</p>
                <p className="text-2xl font-bold text-gray-900">{partner?.total_cases || 0}</p>
              </div>
            </div>
          </div>

          <div 
            className={`bg-white border rounded-2xl p-6 cursor-pointer transition-all ${
              openRequestsCount > 0 
                ? 'border-orange-300 hover:border-orange-400' 
                : 'border-gray-200'
            }`}
            onClick={() => {
              if (openRequestsCount > 0) {
                document.getElementById('cases-list')?.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                openRequestsCount > 0 ? 'bg-orange-100' : 'bg-gray-100'
              }`}>
                <AlertCircle className={`w-6 h-6 ${
                  openRequestsCount > 0 ? 'text-orange-600' : 'text-gray-400'
                }`} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Offene Rückfragen</p>
                <p className={`text-2xl font-bold ${
                  openRequestsCount > 0 ? 'text-orange-600' : 'text-gray-900'
                }`}>
                  {openRequestsCount}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Commission Widget */}
        <div className="mb-8">
          <CommissionWidget 
            partnerId={partnerId} 
            token={localStorage.getItem('partner_token')} 
          />
        </div>

        {/* New Case Button */}
        <div className="mb-8">
          <button
            onClick={() => navigate(`/portal/neuer-fall?partner_id=${partnerId}`)}
            className="new-case-button bg-brand-green hover:bg-brand-green-dark text-white px-8 py-4 rounded-xl font-semibold inline-flex items-center gap-2 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Neuer Fall
          </button>
        </div>

        {/* Cases List */}
        <div id="cases-list" className="bg-white border border-gray-200 rounded-2xl p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Ihre Fälle</h2>
          
          {cases.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Noch keine Fälle erstellt</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">Erstelle deinen ersten Fall, um mit der Schadenbegutachtung zu starten</p>
              <button
                onClick={() => navigate(`/portal/neuer-fall?partner_id=${partnerId}`)}
                className="bg-brand-green hover:bg-brand-green-dark text-white px-6 py-3 rounded-xl font-semibold inline-flex items-center gap-2 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Neuen Fall anlegen
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedCases.map(c => {
                const statusColors = {
                  'draft': 'bg-gray-100 text-gray-700 border-gray-200',
                  'eingegangen': 'bg-brand-green/10 text-brand-green border-brand-green/20',
                  'rueckfrage_offen': 'bg-orange-100 text-orange-700 border-orange-300',
                  'in_bearbeitung': 'bg-blue-100 text-blue-700 border-blue-300',
                  'abgeschlossen': 'bg-green-100 text-green-700 border-green-300'
                };
                const statusColor = statusColors[c.status] || 'bg-gray-100 text-gray-700 border-gray-200';
                
                return (
                  <div 
                    key={c.id} 
                    className={`border-2 rounded-xl p-6 hover:border-brand-green/40 transition-all hover:shadow-md ${
                      c.status === 'rueckfrage_offen' ? 'border-orange-300 bg-orange-50/30' : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <p className="text-xl font-bold text-gray-900">{c.license_plate || 'Kennzeichen nicht erfasst'}</p>
                          {c.status === 'rueckfrage_offen' && (
                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 flex items-center gap-1 border border-orange-300">
                              <AlertCircle className="w-3 h-3" />
                              Rückfrage offen
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                          <span className="flex items-center gap-1">
                            {c.vehicle_manufacturer} {c.vehicle_model}
                          </span>
                          {c.damage_date && (
                            <span className="flex items-center gap-1">
                              • {new Date(c.damage_date).toLocaleDateString('de-DE')}
                            </span>
                          )}
                        </div>
                        {c.status === 'rueckfrage_offen' && (
                          <p className="text-xs text-orange-600 mb-4 font-medium">
                            ⚠️ Bitte ergänzen, damit wir weiterarbeiten können.
                          </p>
                        )}
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/akte/${c.id}?partner_id=${partnerId}`)}
                            className="px-4 py-2 bg-brand-green hover:bg-brand-green-dark text-white text-sm font-semibold rounded-lg transition-colors"
                          >
                            Webakte öffnen
                          </button>
                          <button
                            onClick={() => navigate(`/portal/akte/${c.id}?partner_id=${partnerId}`)}
                            className="px-4 py-2 border-2 border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            Details
                          </button>
                        </div>
                      </div>
                      <span className={`px-4 py-2 rounded-lg text-xs font-bold border-2 ${statusColor}`}>
                        {c.status === 'draft' && 'Entwurf'}
                        {c.status === 'eingegangen' && 'Eingegangen'}
                        {c.status === 'rueckfrage_offen' && 'Rückfrage'}
                        {c.status === 'in_bearbeitung' && 'In Prüfung'}
                        {c.status === 'abgeschlossen' && 'Abgeschlossen'}
                        {!['draft', 'eingegangen', 'rueckfrage_offen', 'in_bearbeitung', 'abgeschlossen'].includes(c.status) && c.status}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      
      {/* Version Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-100 border-t border-gray-200 py-2 px-4 text-center">
        <span className="text-xs text-gray-500">{getVersionString()}</span>
      </div>
    </div>
  );
};

export default DashboardPage;